import { ChangeDetectionStrategy, Component, forwardRef, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { IrisFieldBaseComponent } from '@iris/common/modules/fields/field-base/field-base.component';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { filter, tap } from 'rxjs/operators';
import { ThemePalette } from '@angular/material/core';
import { isNull, isUndefined } from 'lodash';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'iris-field-multiple-state-checkbox',
  templateUrl: './field-multiple-state-checkbox.component.html',
  styleUrls: ['./field-multiple-state-checkbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IrisFieldMultipleStateCheckboxComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class IrisFieldMultipleStateCheckboxComponent extends IrisFieldBaseComponent implements OnInit, ControlValueAccessor {
  @Input() indeterminate = false;

  valueControl: FormControl = new FormControl(null);
  indeterminateEnabled = false;

  ngOnInit(): void {
    this.valueControl.valueChanges
      .pipe(
        filter(() => !this.valueControl.pristine),
        tap(newValue => {
          const nextValue = this.getNextValue(newValue);

          if (this.indeterminate && nextValue === null) {
            this.valueControl.setValue(nextValue, { emitEvent: false });
            this.propagateChange(nextValue);
            this.indeterminateEnabled = true;
            return;
          }
          this.propagateChange(newValue);
        }),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe();
  }

  get checkboxColor(): ThemePalette {
    switch (true) {
      case this.isFalseValue:
        return 'warn';
      case !this.valueControl.value:
        return 'primary';
      default:
        return 'accent';
    }
  }

  get isFalseValue(): boolean {
    return this.valueControl.value === false;
  }

  get isNullValue(): boolean {
    return this.valueControl.value === null;
  }

  writeValue(checked: boolean): void {
    if (isUndefined(checked) && !this.indeterminate) { return; }
    if (this.indeterminate && (isNull(checked) || isUndefined(checked))) {
      this.valueControl.setValue(null, { emitEvent: false });
      this.indeterminateEnabled = true;
    } else {
      this.indeterminateEnabled = false;
      this.valueControl.setValue(checked, { emitEvent: false });
    }
    this.changeDetector.markForCheck();
  }

  getNextValue(value: boolean): boolean {
    switch (true) {
      case this.indeterminateEnabled && value === true:
      case !this.indeterminateEnabled && value === false:
        return false;
      case !this.indeterminateEnabled && value === true:
        return null;
    }
  }
}
